import { useState, useEffect, useId, useRef } from "react";
import classNames from "classnames";
import { useIsCompactLayout } from "@/hooks/useIsCompactLayout";
import Datepicker, { DateValueType } from "react-tailwindcss-datepicker";
import "./DatePicker.css";
import { isValidDate } from "@/lib/dateHelpers";
import { getSingleDateShortcuts, getDateRangeShortcuts } from "@/lib/dateShortcuts";

export interface DateSelectProps {
  value: DateValueType | string;
  onChange: (date: DateValueType | string | null) => void;
  onFocus?: () => void;
  onBlur?: () => void;
  label?: string;
  placeholder?: string;
  displayFormat?: string;
  applyButtonText?: string;
  cancelButtonText?: string;
  useSingleDate?: boolean;
  useMultiCalendar?: boolean;
  showFooter?: boolean;
  supportingText?: string;
  bgClassName?: string;
  minDate?: Date;
  maxDate?: Date;
  error?: boolean;
  errorMessage?: string;
  popoverDirection?: "up" | "down";
  setIsDateSelectorOpen?: (l: boolean) => void;
  disabled?: boolean;
  className?: string;
  shortcuts?: Record<string, { text: string; period: { start: Date; end: Date } }>;
  useDefaultShortcuts?: boolean;
  startFrom?: Date;
  dateLooking?: "forward" | "backward" | "middle";
  hideClearButton?: boolean;
  allowClear?: boolean;
  useModal?: boolean;
  constrainPopover?: boolean;
}

function parseDateValueFromString(value: string): DateValueType {
  const [startDate, endDate] = value.split(" - ");
  return { startDate, endDate };
}

function setDefaultPopoverDirection() {
  return window.innerHeight - (document.getElementById("dateSelector")?.getBoundingClientRect()?.bottom || 0) <
    window.innerHeight / 2
    ? "up"
    : "down";
}

export default function DateSelect({
  value,
  onChange,
  onFocus,
  onBlur,
  label = "",
  placeholder = " ",
  displayFormat = "MMM DD, YYYY",
  applyButtonText = "OK",
  cancelButtonText = "Cancel",
  useSingleDate = false,
  useMultiCalendar = true,
  showFooter = true,
  supportingText,
  bgClassName,
  minDate,
  maxDate,
  error,
  errorMessage,
  popoverDirection,
  setIsDateSelectorOpen,
  disabled = false,
  className,
  shortcuts,
  useDefaultShortcuts = false,
  startFrom,
  dateLooking,
  hideClearButton = false,
  allowClear = true,
  useModal = false,
  constrainPopover = false
}: DateSelectProps) {
  const [isFocused, setIsFocused] = useState(false);
  const [isClearing, setIsClearing] = useState(false);
  const [previousValue, setPreviousValue] = useState<DateValueType | string>(value);
  const dateValue: DateValueType = typeof value === "string" ? parseDateValueFromString(value) : value;
  const { isCompactLayout } = useIsCompactLayout();

  const getEffectiveShortcuts = () => {
    if (shortcuts !== undefined) {
      return shortcuts;
    }

    if (useDefaultShortcuts) {
      return useSingleDate ? getSingleDateShortcuts() : getDateRangeShortcuts();
    }

    return undefined;
  };

  const effectiveShortcuts = getEffectiveShortcuts();

  const shouldShowDualCalendar = !useSingleDate && useMultiCalendar && !isCompactLayout;
  const shouldUseModal = useModal || isCompactLayout;

  const derivedStartFromRef = useRef<Date | undefined>(undefined);
  const hasInitializedRef = useRef(false);

  if (!hasInitializedRef.current && !startFrom && shouldUseModal) {
    const dateToUse = dateValue?.startDate || dateValue?.endDate;
    if (dateToUse) {
      derivedStartFromRef.current = new Date(dateToUse);
      hasInitializedRef.current = true;
    }
  }

  const effectiveStartFrom = startFrom ?? derivedStartFromRef.current;

  useEffect(() => {
    const prevHadValue =
      previousValue &&
      ((typeof previousValue === "string" && previousValue !== "") ||
        (typeof previousValue === "object" && (previousValue.startDate || previousValue.endDate)));

    const currentHasValue =
      value &&
      ((typeof value === "string" && value !== "") ||
        (typeof value === "object" && (value.startDate || value.endDate)));

    if (prevHadValue && !currentHasValue) {
      setIsClearing(true);
      setIsFocused(false);

      // Force blur the input to prevent calendar from opening
      setTimeout(() => {
        const container = document.getElementById("dateSelector");
        const input = container?.querySelector("input");
        if (input && document.activeElement === input) {
          input.blur();
        }
      }, 0);
    }

    setPreviousValue(value);
  }, [value, previousValue]);

  useEffect(() => {
    setIsDateSelectorOpen?.(isFocused && !isClearing);
  }, [isFocused, isClearing, setIsDateSelectorOpen]);

  const inputId = useId();
  return (
    <div
      id="dateSelector"
      data-testid="date-select"
      onMouseDown={() => {
        if (isClearing) {
          setIsClearing(false);
        }
      }}
      onFocus={() => {
        setIsFocused(true);
        onFocus?.();
      }}
      onKeyUp={(e: React.KeyboardEvent<HTMLInputElement>) => {
        if (!allowClear && (e.target as HTMLInputElement).value === "") {
          onChange(value);
        }
      }}
      onBlur={(e) => {
        const isStartDateEvent = isValidDate((e.target as HTMLInputElement).value?.split(" - ")?.[0], "yyyy-MM-dd");
        const isEndDateEvent = isValidDate((e.target as HTMLInputElement).value?.split(" - ")?.[1], "yyyy-MM-dd");

        const isStartDate = isValidDate(dateValue?.startDate, "yyyy-MM-dd");
        const isEndDate = isValidDate(dateValue?.endDate, "yyyy-MM-dd");

        if (
          (!isStartDate && !isEndDate && !allowClear) ||
          (!isStartDateEvent && !isEndDateEvent && (e.target as HTMLInputElement).value !== "")
        ) {
          onChange(value);
        }

        if (!isClearing) {
          setIsFocused(false);
        }
        onBlur?.();
      }}
      className={classNames("date-select relative w-full", className, {
        "filter-active": dateValue?.startDate && dateValue?.endDate,
        "use-modal": shouldUseModal,
        "is-clearing": isClearing,
        "constrain-popover": constrainPopover
      })}
      style={{ minWidth: useSingleDate ? "160px" : "280px" }}
    >
      <Datepicker
        startFrom={effectiveStartFrom}
        disabled={disabled}
        value={dateValue}
        dateLooking={dateLooking}
        onChange={(newValue) => {
          if (typeof newValue === "object" && newValue !== null) {
            const hasValue = newValue.startDate || newValue.endDate;
            if (!hasValue) {
              onChange({ startDate: null, endDate: null });
              return;
            }
          }
          onChange(newValue);
        }}
        inputId={inputId}
        separator="-"
        placeholder={placeholder}
        popoverDirection={shouldUseModal ? "down" : (popoverDirection ?? setDefaultPopoverDirection())}
        displayFormat={displayFormat}
        showFooter={showFooter}
        showShortcuts={!!effectiveShortcuts}
        useRange={shouldShowDualCalendar}
        asSingle={useSingleDate}
        toggleClassName={hideClearButton ? "hidden" : ""}
        inputClassName={classNames(
          "w-full h-[56px] pl-4 border text-sys-brand-on-surface placeholder-sys-brand-outline border-sys-brand-outline outline-sys-brand-outline bg-sys-brand-surface rounded-[4px]",
          {
            "border-sys-brand-primary outline-sys-brand-primary": isFocused && !error
          },
          {
            "!border-sys-brand-error !outline-sys-brand-error text-sys-brand-error": error
          },
          {
            "!bg-sys-brand-disabled  text-sys-brand-disabled outline-sys-brand-disabled opacity-50": disabled
          },
          {
            "cursor-pointer": !disabled
          },
          bgClassName
        )}
        configs={{
          shortcuts: effectiveShortcuts,
          footer: {
            cancel: cancelButtonText,
            apply: applyButtonText
          }
        }}
        minDate={minDate}
        maxDate={maxDate}
        readOnly={shouldUseModal && !isClearing}
      />
      {label && (
        <label
          htmlFor={inputId}
          className={classNames(
            "absolute px-1 -top-[8px] left-[12px] bg-sys-brand-surface text-body-small text-sys-brand-on-surface",
            {
              "text-sys-brand-primary": isFocused,
              "!text-sys-brand-error ": error,
              "text-opacity-50": disabled
            },
            bgClassName
          )}
        >
          {label}
        </label>
      )}

      {(error || supportingText) && (
        <div
          className={classNames(
            "text-body-small ml-4 mt-1 whitespace-pre-line",
            error ? "text-sys-brand-error" : "text-sys-brand-on-surface",
            !error && disabled && "opacity-50",
            bgClassName
          )}
        >
          {error ? errorMessage : supportingText}
        </div>
      )}
    </div>
  );
}
